'use client';
/**
 * more-tidy — PC 헤더 「전체 메뉴」(종전 「더보기」 · 761px 이상 · 모바일은 하단 탭 「더보기」 → /more).
 *
 *   · 단일 출처 = nav-config MORE_GROUPS(/more 와 같은 묶음 · 같은 순서 · 광고 맨 앞).
 *   · 버튼(aria-expanded · aria-controls) + 패널. Esc 닫기(버튼으로 포커스 복귀) · 바깥 클릭 닫기 ·
 *     포커스가 메뉴 밖으로 나가면 닫기 · 경로가 바뀌면 닫기. 열 때 포커스는 버튼에 남는다(Tab 으로 패널 첫 링크 — DOM 바로 다음).
 *   · 패널 링크는 서버 HTML 에 전부 있다(닫힘 = hidden 속성). 방문자·UA 분기 없음 — 같은 URL = 같은 HTML.
 *   · 패널은 마스트(.kd-mast · sticky) 기준 전폭 — 헤더 높이 불변(버튼은 다른 메뉴와 같은 44px 줄).
 *   · 외부 링크(카톡방)는 헤더 패널에서 뺀다(헤더 HTML 에 외부 링크 0 — /more 에는 있다). 글자 크기 조작도 /more 에만.
 *   · 묶음 제목은 <p>(h2 아님) — 모든 쪽 서버 HTML 에 실리는 헤더라 문서 제목 구조(h1·h2)를 흐리지 않는다.
 *   · 계측: 링크마다 data-nav-ev="more_click" + data-nav-group — NavV2Effects 위임 리스너(광고 묶음은 advertise_click 도).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { MORE_GROUPS } from '@/lib/nav/nav-config';
import { navEvAttrs } from '@/lib/nav/nav-track';
// L6 tabs-r1 — 버튼 라벨 「더보기」→「전체 메뉴」 · 패널 첫 줄 = 바로가기 타일 8(/more 와 같은 조각) · 우측 1열 광고 카드(숫자는 서버 prop 이 올 때만).
import { MoreAdCard, MoreQuickTiles } from './MoreQuick';
import './nav-more-r1.css';

export default function NavMoreMenu({ active = false, visitors30d = null }: { active?: boolean; /** L6 — 광고 카드 「30일 사람 조회 N회」(서버가 넘길 때만 · 클라이언트 트리라 직접 읽지 않는다). */ visitors30d?: number | null }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelId = `kd-mm-${useId().replace(/:/g, '')}`;
  const pathname = usePathname();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  }, []);

  // 경로가 바뀌면 닫는다(링크로 이동한 뒤 패널이 남지 않게).
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(true); }
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  const onBlur = (e: React.FocusEvent<HTMLLIElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next && rootRef.current && !rootRef.current.contains(next)) setOpen(false);
  };

  return (
    <li className="kd-mm" ref={rootRef} onBlur={onBlur} data-nav-dd="more">
      <button
        ref={btnRef}
        type="button"
        className="kd-mm__btn"
        aria-expanded={open}
        aria-controls={panelId}
        data-active={active ? '' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        전체 메뉴
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div id={panelId} className="kd-mm__panel" hidden={!open} role="region" aria-label="전체 메뉴">
        <div className="kd-mm-top">
          <MoreQuickTiles className="kd-mm-quick" />
          <MoreAdCard visitors30d={visitors30d} className="kd-mm-adcard" />
        </div>
        <div className="kd-mm__grid">
          {MORE_GROUPS.filter((g) => g.key !== 'ad').map((g) => {
            const items = g.items.filter((it) => !it.external);
            if (!items.length) return null;
            const hid = `${panelId}-${g.key}`;
            return (
              <div key={g.key} role="group" className={g.key === 'ad' ? 'kd-mm__group kd-mm__group--ad' : 'kd-mm__group'} aria-labelledby={hid} data-more-group={g.key}>
                <p id={hid} className="kd-mm__h">{g.title}</p>
                {g.key === 'ad' ? <p className="kd-mm__desc">{g.desc}</p> : null}
                <ul className="kd-mm__list">
                  {items.map((it) => (
                    <li key={it.id}>
                      <a href={it.href} className="kd-mm__item" rel={it.nofollow ? 'nofollow' : undefined} data-nav-group={g.key} data-kd-region-link={it.regionLink} {...navEvAttrs('more_click', it.id)}>
                        {it.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <a href="/more" className="kd-mm__all" {...navEvAttrs('nav_click', 'more_all')}>전체 메뉴 보기</a>
      </div>
    </li>
  );
}
